<script setup>
import {Chart} from 'highcharts-vue'
import Highcharts from 'highcharts'
import exportingInit from "highcharts/modules/exporting"
import addNetworkgraph from "highcharts/modules/networkgraph"
import dashboardService from "@/services/dashboard.service";
import {getLogo} from "@/helpers/logo";
import {ref, reactive, inject, getCurrentInstance, nextTick, watch, computed} from "vue";
import graphOptions from './graphOptions';
import useDebounce from "@/composables/useDebounce";
import PeriodFilter from "@/views/market/PeriodFilter.vue";

typeof exportingInit === 'function' && exportingInit(Highcharts);
typeof addNetworkgraph === 'function' && addNetworkgraph(Highcharts);

const instance = getCurrentInstance();
window.addToTracker = function (startupId) {
    instance.proxy.$emit('addToTracker', startupId);
};

window.showSimilar = function (startupId) {
    instance.proxy.$emit('showSimilar', startupId);
};

window.showPartnerships = function (corporate) {
    instance.proxy.$emit('showPartnerships', corporate);
};

const props = defineProps(['searchType','searchObjectId','startupsNodes']);

Highcharts.AST.allowedAttributes.push('onclick');
const debounce = useDebounce();

const periods = [
    {label: 'All time', value: 'all'},
    {label: 'Current year', value: 'current'},
    {label: 'Last year', value: 'last'},
    {label: 'Last 3 years', value: 'last3'},
    {label: 'Custom period', value: 'custom'},
]

const chartOptions = reactive(graphOptions);
const counts = inject('counts');
const startupsIds = inject('startupsIds');

const companiesForFilter = ref([]);
const watchCompaniesFilter = ref(true);
const companySearch = ref('');
const period = ref(periods[0]);
const customPeriod = ref([null, null]);
const showCompaniesFilterList = ref(false);
const showPeriodsFilter = ref(false);

const maxCompaniesInFilter = computed(() => props.startupsNodes ? 30 : 50);
const selectedCompanies = computed(() => companiesForFilter.value.filter(item => item.checked));

const currentPeriodLabel = computed(() => {
    if (period.value.value === 'custom' && customPeriod.value[0] && customPeriod.value[1]) {
        return `${customPeriod.value[0]} - ${customPeriod.value[1]}`;
    }

    return periods.find(item => item.value === period.value.value).label;
});

const selectAll = computed({
    get() {
        return companiesForFilter.value.every(item => item.checked);
    },

    set(value) {
        companiesForFilter.value.forEach(item => {
            item.checked = value;
        });
    }
});

const hasSearchResults = computed(() => {
    return companiesForFilter.value.some(company => company.name.toLowerCase().includes(companySearch.value.toLowerCase()));
});

watch(companiesForFilter, () => {
    if (watchCompaniesFilter.value) {
        debounce(() => getData(), 1000);
    }
}, {deep: true});

watch(props, () => {
    reset();
}, {immediate: true, deep: true});

function reset() {
    setCompaniesFilter([]);
    getData()
}

async function getData() {
    chartOptions.series = [];

    let filters = {
        ...props,
        years: period.value.value,
        customPeriod: customPeriod.value,
    };

    if (companiesForFilter.value.length) {
        filters.companiesIds = companiesForFilter.value.filter(item => item.checked).map(item => item.id);
    }

    const {data} = await dashboardService.getChartData('PartnershipsGraph', filters);

    let series = data.series;

    series.nodes = series.nodes.map(node => {
        return {
            ...node,
            marker: 'symbol' in node.marker ? {
                ...node.marker,
                symbol: 'url(' + getLogo(node.marker.symbol) + ')'
            } : node.marker
        }
    });

    chartOptions.series.push(series);
    counts.value = data.counts;
    startupsIds.value = data.startups_ids;

    if (!companiesForFilter.value.length) {
        setCompaniesFilter(data.companies_for_filter.map((item, i) => ({
            ...item,
            checked: i < maxCompaniesInFilter.value,
        })));
    }
}

function setCompaniesFilter(value) {
    watchCompaniesFilter.value = false;
    companiesForFilter.value = value;

    nextTick(() => watchCompaniesFilter.value = true);
}

function setPeriod([value, customValue]) {
    period.value = value;
    customPeriod.value = customValue;
    showPeriodsFilter.value = false;
}

</script>

<template>
    <div>
        <div style="padding: 10px; display: flex; justify-content: end;">
            <div class="graph-container__top" style="">
                <div class="filters__list-market" v-click-outside="() => {showPeriodsFilter = false}">
                    <a class="filters__list__title" @click="showPeriodsFilter = !showPeriodsFilter">{{currentPeriodLabel}}</a>

                    <b-collapse v-model="showPeriodsFilter" class="filters__list__content" style="position: absolute;">
                        <PeriodFilter
                            :periods="periods"
                            @apply="setPeriod"
                        />
                    </b-collapse>
                </div>
                <div
                    v-if="companiesForFilter.length"
                    style=""
                    class="filters__list-market ml-2"
                    v-click-outside="() => {showCompaniesFilterList = false}"
                >
                    <a class="filters__list__title" @click="showCompaniesFilterList = !showCompaniesFilterList">
                        {{selectedCompanies.length}} of {{companiesForFilter.length}}

                        <template v-if="startupsNodes">
                            startups
                        </template>

                        <template v-else>
                            enterprises
                        </template>
                    </a>

                    <b-collapse v-model="showCompaniesFilterList" class="filters__list__content" style="position: absolute;">
                        <div class="filters__list-market__search">
                            <input :placeholder="`Search for ${startupsNodes ? 'startups' : 'enterprises'}`" type="text" v-model="companySearch">
                        </div>
                        <ul class="filters__list__choose" style="max-height: 300px; overflow-y: auto;">
                            <li
                                v-for="(company, index) in companiesForFilter"
                                v-show="company.name.toLowerCase().includes(companySearch.toLowerCase())"
                                :key="company.id"
                                class="input-checkbox"
                            >
                                <input :id="`filter-company-${company.id}`" type="checkbox" v-model="companiesForFilter[index].checked">
                                <label :for="`filter-company-${company.id}`">{{company.name}}</label>
                            </li>
                        </ul>
                        <div v-if="!hasSearchResults">
                            No Results
                        </div>

                        <footer class="filters__list__content__footer">
                            <div class="input-checkbox">
                                <input type="checkbox" v-model="selectAll" id="fic">
                                <label for="fic" class="m-0">Select All</label>
                            </div>
                        </footer>
                    </b-collapse>
                </div>
            </div>
        </div>

        <Chart
            v-if="counts.partnerships"
            :options="chartOptions"
        />

        <div v-else class="graph-partnership-empty">
            <p>No partnerships in the selected period</p>
        </div>
    </div>
</template>

<style scoped>
    .graph-container__top {
        position: relative;
        z-index: 2;
        display: flex; 
        justify-content: center;
        flex-wrap: wrap;
        align-items: center; 
        gap: 10px;
        margin-top: 7px;
        margin-right: 30px;
    }
    @media (min-width: 1025px) {
        .graph-container__top {
            position: absolute; 
        }
    }
</style>
